import { logError } from '~/logger';
import text from '~/text';
import mutationCopyHeroBuild from '~/graphql/hero/mutationCopyHeroBuild';
import { findHeroBuildById } from '~/helpers/findHeroBuildById';
import messages, { HeroBuildStoreMessage } from '../messages';

/**
 * Generates a unique copy name for a hero build
 * If the original has a name, appends (2), (3), etc.
 * If the original has no name, uses "Basic (N)" where N is the copy number (localized)
 */
function generateCopyName(
  originalName: string | undefined,
  existingBuilds: Array<{ build_display_name?: string }>
): string {
  let baseName = originalName || text('heroes.basic');

  // Strip existing " (N)" suffix if present to get the true base name
  // This ensures "Foo (2)" becomes "Foo", so copying it gives "Foo (3)" not "Foo (2) (2)"
  const stripPattern = /\s*\(\d+\)$/;
  baseName = baseName.replace(stripPattern, '');

  // Find all builds with similar names to determine the next number
  const escapedBaseName = baseName.replace(/[()]/g, '\\$&');
  const copyPattern = new RegExp(`^${escapedBaseName}\\s*\\((\\d+)\\)$`);
  let maxNumber = 1;

  for (const build of existingBuilds) {
    const buildName = build.build_display_name || text('heroes.basic');

    // Check if it matches the base name exactly (this would be copy #2)
    if (buildName === baseName) {
      maxNumber = Math.max(maxNumber, 2);
    }

    // Check if it matches the pattern "Name (N)"
    const match = buildName.match(copyPattern);
    if (match) {
      const num = parseInt(match[1], 10);
      maxNumber = Math.max(maxNumber, num + 1);
    }
  }

  return `${baseName} (${maxNumber})`;
}

export default async function copyHeroBuild(
  {
    playerId,
    heroBuilds,
    setHeroBuilds,
    emit,
  }: {
    playerId: string | null;
    heroBuilds: any;
    setHeroBuilds: (builds: any) => void;
    emit: (message: HeroBuildStoreMessage, data?: any) => void;
  },
  { hero_handle, hero_build_id }: { hero_handle: string; hero_build_id: string }
) {
  if (!playerId) {
    throw new Error('Player ID is required');
  }

  try {
    // Get the build to copy and generate new name
    const existingBuilds = heroBuilds[hero_handle] || [];
    const buildToCopy = findHeroBuildById(existingBuilds, hero_build_id);
    const newBuildName = generateCopyName(buildToCopy?.build_display_name, existingBuilds);

    const result = await mutationCopyHeroBuild({
      playerId,
      heroHandle: hero_handle,
      heroBuildId: hero_build_id,
      newBuildName,
    });

    // Update hero builds with the result
    const updatedHeroBuilds = { ...heroBuilds };
    updatedHeroBuilds[result.heroHandle] = result.heroBuilds;
    setHeroBuilds(updatedHeroBuilds);

    emit(messages.GOT_HERO_BUILDS, updatedHeroBuilds);
    emit(messages.NEW_HERO_BUILD_CREATED);
  } catch (err) {
    logError(err, {
      module: 'HeroBuildStore',
      func: 'copyHeroBuild',
      playerId,
      hero_handle,
      hero_build_id,
    });
  }
}
